/* eslint-disable no-unneeded-ternary */
/* eslint-disable no-param-reassign */
import { FC, useRef, useState } from 'react'
import {
  Button,
  CloseButton,
  Flex,
  Text,
  Box,
  Checkbox,
  Badge,
  Divider
} from '@chakra-ui/react'
import { v4 as uuidV4 } from 'uuid'
import { useTranslation } from 'react-i18next'
import { FiEdit, FiGrid, FiSave } from 'react-icons/fi'
import { useDrag, useDrop } from 'react-dnd'
import { Input } from '../../Form/Input'
import { useDnd } from '../../../hooks/useDnd'

interface InputBuildProps {
  data: {
    id: string
    name: string
    isRequired: boolean
    placeholder: string
    type: string
    value: string
    defaultValue: string
    label: string
    error?: boolean
  }
  onExclude?: () => void
  index: number
  indexList: number
}

interface DropAndDragItem {
  type: string
  id: string | number
  index: number
  indexList: number
  name: string
}

export const InputBuild: FC<InputBuildProps> = ({
  data,
  onExclude,
  index,
  indexList
}) => {
  const [t] = useTranslation('pageCreatingForms')
  const [isRequired, setIsRequired] = useState(data.isRequired)
  const [placeholder, setPlaceholder] = useState(data.placeholder)
  const [defaultValue, setDefaultValue] = useState(data.defaultValue)
  const [label, setLabel] = useState(data.label)
  const [showEdit, setShowEdit] = useState(false)
  const [error, setError] = useState(data?.error)

  const { MoveForm, listForms, setListForms } = useDnd()
  const ref = useRef<any>(null)
  const styledGrabbing = {
    border: '2px dashed rgba(0, 0, 0, 0.2)',
    pt: '5px',
    borderRadius: '0',
    bg: '#d3d3d3',
    cursor: 'grabbing'
  }
  const [{ isDragging }, dragRef] = useDrag({
    collect: (monitor) => ({
      isDragging: monitor.isDragging()
    }),
    type: 'GROUP',
    item: { type: 'GROUP', id: data?.id, index, name: data?.name, indexList }
  })

  const [, dropRef] = useDrop({
    accept: 'GROUP',
    hover(item: DropAndDragItem, monitor) {
      const draggedListIndex = item.indexList
      const targetListIndex = indexList

      const draggedIndex = item.index
      const targetIndex = index

      if (
        draggedIndex === targetIndex &&
        draggedListIndex === targetListIndex
      ) {
        return
      }

      const targetSize = ref?.current.getBoundingClientRect()
      const targetCenter = (targetSize.bottom - targetSize.top) / 2

      const draggedOffset = monitor.getClientOffset()
      const coordY = draggedOffset !== undefined ? draggedOffset?.y : 1
      const coordX = draggedOffset !== undefined ? draggedOffset?.x : 1
      const draggedTop = coordY !== undefined ? coordY - targetSize.top : 1

      if (draggedIndex < targetIndex && draggedTop < targetCenter) {
        return
      }

      if (draggedIndex > targetIndex && draggedTop > targetCenter) {
        return
      }

      MoveForm(draggedListIndex, targetListIndex, draggedIndex, targetIndex)

      item.index = targetIndex
      item.indexList = targetListIndex
    }
  })

  dragRef(dropRef(ref))

  const handlerChangeFieldValue = () => {
    const group = listForms[indexList]
    const object = {
      isRequired,
      defaultValue,
      name: uuidV4(),
      label,
      placeholder,
      maxLength: 150,
      error: label !== '' ? false : true
    }

    const fields = group.fields.map((field) => {
      return field.id === data.id ? { ...field, ...object } : field
    })

    const objectField = {
      fields
    }

    const newGroupValues = listForms.map((groupList, indexGroup) => {
      return indexList === indexGroup
        ? { ...groupList, ...objectField }
        : groupList
    })

    return newGroupValues
  }

  return (
    <Box
      w="100%"
      ref={ref}
      style={isDragging ? { opacity: 0.4 } : undefined}
      border={error ? '1px solid #E53E3E' : '1px solid #E2E8F0'}
      borderRadius={8}
      bg="white"
      overflow="hidden"
      boxShadow="sm"
    >
      {/* Field header row */}
      <Flex
        w="100%"
        px="4"
        py="3"
        justifyContent="space-between"
        alignItems="center"
        bg={error ? 'red.50' : 'gray.50'}
        borderBottom={showEdit ? '1px solid #E2E8F0' : 'none'}
      >
        <Flex gap={3} alignItems="center" flex={1} minW={0}>
          <Box color="gray.400" cursor="grab" flexShrink={0}>
            <FiGrid size={18} />
          </Box>
          <Text
            fontWeight="medium"
            fontSize="sm"
            color={label === '' ? 'gray.400' : 'gray.700'}
            noOfLines={1}
          >
            {label === '' ? t('errors.type.1') : label}
          </Text>
          <Badge colorScheme="blue" fontSize="10px" flexShrink={0}>
            {t('fields.1')}
          </Badge>
          <Text fontSize="xs" color="gray.400" flexShrink={0}>
            150 {t('characters')}
          </Text>
          {isRequired && (
            <Badge colorScheme="orange" fontSize="10px" flexShrink={0}>
              {t('isRequired')}
            </Badge>
          )}
        </Flex>

        <Flex gap={2} flexShrink={0} ml={3}>
          <Button
            colorScheme={showEdit ? 'blue' : 'green'}
            size="xs"
            leftIcon={showEdit ? <FiSave /> : <FiEdit />}
            onClick={() => {
              setShowEdit(!showEdit)
              if (showEdit) {
                const newValuesGroups = handlerChangeFieldValue()
                setListForms(newValuesGroups)
              }
            }}
          >
            {showEdit ? t('saveEdit') : ''}
          </Button>
          <CloseButton
            size="sm"
            bg="red.500"
            color="white"
            borderRadius="md"
            _hover={{ bg: 'red.600' }}
            onClick={() => onExclude?.()}
          />
        </Flex>
      </Flex>

      {/* Edit panel */}
      {showEdit && (
        <Box px="4" py="4">
          <Flex direction="column" gap={4}>
            <Input
              name="label"
              label={`${t('question')}`}
              value={label}
              onChange={(event) => setLabel(event.target.value)}
            />
            <Flex alignItems="center" gap={3}>
              <Checkbox
                id={`required-${data.id}`}
                name="isRequired"
                defaultChecked={isRequired}
                colorScheme="green"
                onChange={(event) => setIsRequired(event.target.checked)}
              />
              <Text
                as="label"
                htmlFor={`required-${data.id}`}
                fontSize="sm"
                color="gray.600"
                cursor="pointer"
                userSelect="none"
              >
                {t('questionIsRequired')}
              </Text>
            </Flex>
          </Flex>
        </Box>
      )}
    </Box>
  )
}
